import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject, combineLatest } from 'rxjs';
import { delay, distinctUntilChanged, filter, map, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { Customers } from '../../../account-info-header/models/AccountInfoApiResponse';
import { selectActiveWireInstructions, selectEAuthCustomers, selectIsPtrs, selectMasterAccount, selectSubAccountId, selectTemplateStartTransactionStarted, selectTemplatesList, selectTemplatesListEmpty, selectTemplatesListForm, selectHasBundled } from '../../../account-info-header/reducers';
import { ModifiedFirstPartiesActions } from '../../../modified-first-parties/actions';
import * as radioModels from '../../../shared/controlModels/radioModels';
import { selectStandingAuthTableStartTransactionClickedWire } from '../../../shared/reducers';
import { StandingInstruction } from '../../../shared/types/StandingInstruction';
import { PtrsActions, StandingAuthorizationsActions, StandingAuthorizationsTableActions, TransactionFormActions, WireActions } from '../../actions';
import { StandingAuthorizationsRequest } from '../../models/Standing-Authorizations';
import { RadioControl } from '../../models/form/FormControlRadio';
import { WireExtraPrompts } from '../../models/form/TransactionForm';
import { Drafts } from '../../models/wire/WireTemplatesResponse';
import { selectIsFromEdit, selectPtrsCallFinished, selectRequestingAccountHolderId, selectStandingAuthCallSucceeded, selectStandingAuthId, selectTemplateId, selectUseStandingAuth, selectUseTemplate, selectUseTemplateVal, selectWireInputV3CallFinished, selectWireUseStandingAuthVal } from '../../reducers';
import { buildFromPrefilledForm, createControl } from '../../utilities/formUtilities';

@Component({
  standalone: false,
  selector: 'mm-wire-extra-prompts',
  templateUrl: './wire-extra-prompts.component.html',
  styleUrls: ['./wire-extra-prompts.component.scss'],
})
export class WireExtraPromptsComponent implements OnInit, OnDestroy {
  @Input() parentForm!: UntypedFormGroup;
  @Input() palAccountInContext = false;
  @Input() prefilledPrompts!: WireExtraPrompts | undefined;
  @Input() prefilledPromptsObs: Observable<WireExtraPrompts>;
  @Output() extraPromptsComplete = new EventEmitter<boolean>();
  @Output() extraPromptsUpdatedInEdit = new EventEmitter();
  private store = inject(Store);
  private fb = inject(UntypedFormBuilder);
  private suppressEditHydrationSelectionSideEffects = false;
  private expectedHydrationSelection: string | null = null;
  standingAuthorizations$: Observable<StandingInstruction[]>;
  templates$: Observable<Drafts[]>;
  customers$: Observable<Customers[]>;
  destroy$ = new Subject<boolean>();
  showStandingAuthSelection: boolean = false;
  showRequestingAccountHolder: boolean = false;
  requestingAccountHolderCount: number = 0;
  isPtrs: boolean;
  subAccountId: string;
  masterAccountId: string;
  enableWireTemplates: boolean = false;
  hasBundledOffer: boolean = false;
  useStandingAuthModel: RadioControl = radioModels.useStandingAuthModel;
  standingAuthSelection: string;
  useBundledPromptsSelected: boolean = false;
  useSloa$: Observable<boolean>;

  bsloaModel: RadioControl = {
    title: 'Use a standing authorization?',
    controlName: 'useStandingAuth',
    radios: [
      {
        value: 'yes',
        label: ['Yes, use a standing authorization'],
      },
      {
        value: 'bundled',
        label: ['Yes, use a bundled standing authorization'],
      },
      {
        value: 'no',
        label: ['No, create a one-time or recurring transaction that will require client authorization'],
      },
    ],
    validationMessage: 'Select an option'
  };
  useBundledModel: RadioControl = {
    title: 'Select one of the following',
    controlName: 'useBundledStandingAuth',
    radios: [
      {
        value: 'yes',
        label: ['Bundled standing authorization profile'],
      },
      {
        value: 'no',
        label: ['One-time (using firm\'s bundled authorization)'],
      }
    ],
    validationMessage: 'Select an option'
  };

  ngOnInit(): void {
    const wireFormGroup = this.fb.group({ useStandingAuth: [this.palAccountInContext ? 'no' : '', Validators.required], });
    this.parentForm.setControl('extraPrompts', wireFormGroup);
    this.store.select(selectHasBundled).pipe(takeUntil(this.destroy$))
      .subscribe((hasBundled) => this.hasBundledOffer = hasBundled);
    this.store.select(selectIsPtrs).pipe(takeUntil(this.destroy$))
      .subscribe((isPtrs) => this.isPtrs = isPtrs);
    this.store.select(selectSubAccountId).pipe(takeUntil(this.destroy$))
      .subscribe((subAccountId) => this.subAccountId = subAccountId);
    this.store.select(selectMasterAccount).pipe(takeUntil(this.destroy$))
      .subscribe((masterAccountId) => this.masterAccountId = masterAccountId);

    this.standingAuthorizations$ = this.store.select(selectActiveWireInstructions);
    this.templates$ = this.store.select(selectTemplatesListForm);

    this.customers$ = this.store.select(selectEAuthCustomers);

    const standingAuthSelection$ = this.store.select(selectWireUseStandingAuthVal).pipe(
      distinctUntilChanged(),
      filter((val) => val !== '')
    );

    // DECIDE WHETHER TO SHOW REQUESTING ACCOUNT HOLDER SELECTOR OR CALL LOAD POPUP
    combineLatest([
      standingAuthSelection$,
      this.store.select(selectIsFromEdit),
      this.store.select(selectEAuthCustomers),
      this.store.select(selectTemplatesListEmpty)
    ])
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        filter(([standingAuthSelection, isFromEdit, _customers, _noTemplates]) => !isFromEdit || standingAuthSelection === 'no')
      )
      .subscribe(([standingAuthSelection, isFromEdit, customers, noTemplates]) => {
        this.standingAuthSelection = standingAuthSelection;
        if (standingAuthSelection === 'no') {
          this.showStandingAuthSelection = false;
          this.useBundledPromptsSelected = false;

          if (this.useBundledStandingAuth) {
            this.extraPromptsGroup.removeControl('useBundledStandingAuth');
          }

          if (this.palAccountInContext) {
            this.showRequestingAccountHolder = false;
            this.requestingAccountHolderCount = 0;
            this.enableWireTemplates = !noTemplates;
            return;
          }

          this.showRequestingAccountHolder = customers?.length > 0;
          this.requestingAccountHolderCount = customers?.length;
          if (!customers?.length) {
            this.enableWireTemplates = !noTemplates;
            if (!this.enableWireTemplates && !isFromEdit) {
              this.callWireInputV3('');
            }
          }
        }
        if (standingAuthSelection === 'bundled-no') {
          this.showRequestingAccountHolder = false;
          this.enableWireTemplates = false;

          if (!this.palAccountInContext) {
            this.callWireInputV3('');
          }
        }
        // Ensures that we have a hydration selection when coming from the edit flow so we can supress the side effects call
        const isHydrationSelection = this.suppressEditHydrationSelectionSideEffects
          && !!this.expectedHydrationSelection
          && (this.standingAuthSelection === this.expectedHydrationSelection
            || (
              this.expectedHydrationSelection.startsWith('bundled')
              && this.standingAuthSelection === 'bundled'
            )
          );
        if (standingAuthSelection === 'bundled') {
          createControl(this.extraPromptsGroup, 'useBundledStandingAuth', '', [Validators.required]);
          this.useBundledPromptsSelected = true;
          this.showRequestingAccountHolder = false;
        }
        if (standingAuthSelection === 'yes') {
          this.enableWireTemplates = false;
          // have to manually remove template controls because they can be added to the form when 'start transaction' from table is clicked
          // addresses this issue: SAC-318380
          if (this.useTemplates) {
            this.extraPromptsGroup.removeControl('useTemplates');
          }
          if (this.selectedTemplateControl) {
            this.extraPromptsGroup.removeControl('templateId');
          }
          if (this.useBundledStandingAuth) {
            this.extraPromptsGroup.removeControl('useBundledStandingAuth');
          }

          this.showRequestingAccountHolder = false;
          this.useBundledPromptsSelected = false;

          // Makes the side effects call unless coming from edit flow
          if (!isHydrationSelection) {
            this.callStandingAuthorizations();
          }
        }
        if (standingAuthSelection === 'bundled-yes') {
          // have to manually remove template controls because they can be added to the form when 'start transaction' from table is clicked
          // addresses this issue: SAC-318380
          if (this.useTemplates) {
            this.extraPromptsGroup.removeControl('useTemplates');
          }
          if (this.selectedTemplateControl) {
            this.extraPromptsGroup.removeControl('templateId');
          }

          this.showRequestingAccountHolder = false;

          // Makes the side effects call unless coming from edit flow
          if (!isHydrationSelection) {
            this.callStandingAuthorizations(true);
          }
        }

        // Once the expected hydration is selected coming from edit, we want to turn off side effect suppression to resume normal behavior
        if (isHydrationSelection && standingAuthSelection === this.expectedHydrationSelection) {
          this.suppressEditHydrationSelectionSideEffects = false;
          this.expectedHydrationSelection = null;
        }
      });

    this.useSloa$ = this.store.select(selectUseStandingAuth);

    this.store.select(selectUseTemplateVal)
      .pipe(
        filter((val: 'yes' | 'no' | '') => val !== ''),
        takeUntil(this.destroy$),
        withLatestFrom(
          this.store.select(selectIsFromEdit),
          this.store.select(selectEAuthCustomers),
          this.store.select(selectPtrsCallFinished)
        ),
        filter(([useTemplateSelection, isFromEdit, _customers, ptrsCallFinished]) =>
          useTemplateSelection === 'no'
          && !isFromEdit
          && (!this.palAccountInContext || !this.isPtrs || ptrsCallFinished)
        )
      )
      .subscribe(() => {
        if (this.palAccountInContext) {
          this.callWireInputPAL();
        } else {
          this.callWireInputV3('');
        }
        this.store.dispatch(TransactionFormActions.saveTemplateSelectionChanged());
      });

    // WIRE INPUT CALL
    this.store.select(selectRequestingAccountHolderId)
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(
          this.store.select(selectIsFromEdit),
          this.store.select(selectStandingAuthTableStartTransactionClickedWire),
          this.store.select(selectTemplatesListEmpty)
        ),
        filter(([requestingAccountHolderId, isFromEdit, startTransactionClicked, _noTemplates]) =>
          !this.palAccountInContext && !startTransactionClicked && !!requestingAccountHolderId
        ),
      )
      .subscribe(([requestingAccountHolderId, isFromEdit, startTransactionClicked, noTemplates]) => {
        this.enableWireTemplates = !noTemplates;
        if (!this.enableWireTemplates && !isFromEdit) {
          this.callWireInputV3('');
        }
      });


    // STANDING AUTH CONTROL DISPLAY BLOCK
    combineLatest([standingAuthSelection$, this.store.select(selectStandingAuthCallSucceeded)])
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(this.store.select(selectStandingAuthTableStartTransactionClickedWire)),
        filter(([[_selection, _callFinished], startTransactionClicked]) => !startTransactionClicked),
        tap(([[selection, _callFinished], _startTransactionClicked]) => this.standingAuthSelection = selection)
      )
      .subscribe(([[selection, callFinished], _startTransactionClicked]) => {
        if (selection === 'bundled') {
          this.useBundledPromptsSelected = true;
        }

        if ((selection === 'yes' || selection === 'bundled-yes') && callFinished) {
          this.showStandingAuthSelection = true;
        } else {
          this.showStandingAuthSelection = false;
        }
      });


    //STANDING AUTH INPUT CALL
    this.store.select(selectStandingAuthId)
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(
          this.store.select(selectIsFromEdit),
          this.store.select(selectStandingAuthTableStartTransactionClickedWire)
        ),
        filter(([_standingAuthId, isFromEdit, startTransactionClicked]) =>
          !this.palAccountInContext && !isFromEdit && !startTransactionClicked
        ),
        map(([standingAuthId, _isFromEdit, _startTransactionClicked]) => standingAuthId),
        filter((val) => val !== ''),
        distinctUntilChanged()
      )
      .subscribe((standingAuthId) => {
        this.callWireInputV3(standingAuthId);
      });

    const templateId$ = this.store.select(selectTemplateId).pipe(
      takeUntil(this.destroy$),
      withLatestFrom(
        this.store.select(selectIsFromEdit),
        this.store.select(selectTemplatesList)
      ),
      filter(([_, isFromEdit, _list]) => !isFromEdit)
    );

    // EXTRA PROMPTS COMPLETE BLOCK
    const extraPromptsReady$ = combineLatest([
      this.extraPromptsGroup?.statusChanges,
      this.store.select(selectWireInputV3CallFinished),
      this.store.select(selectPtrsCallFinished)
    ]).pipe(
      takeUntil(this.destroy$),
      map(([status, inputCallV3Finished, ptrsCallFinished]) => {
        const isValid = status === 'VALID';
        return this.areExtraPromptsReady(
          isValid,
          this.isPtrs,
          ptrsCallFinished,
          inputCallV3Finished
        );
      }),
      distinctUntilChanged(),
    );

    extraPromptsReady$
      .pipe(
        withLatestFrom(
          this.store.select(selectUseTemplate),
          this.store.select(selectIsFromEdit)
        ),
        filter(([, useTemplate, isFromEdit]) => !useTemplate || isFromEdit || this.palAccountInContext),
        map(([complete]) => complete)
      )
      .subscribe((complete) => {
        this.extraPromptsComplete.emit(complete);
      });

    // EDIT
    this.store.select(selectIsFromEdit)
      .pipe(
        takeUntil(this.destroy$),
        filter((isFromEdit) => isFromEdit)
      )
      .subscribe((_fromEdit) => {
        // Save the standing auth selection before building the form because building the form will reset the selection
        this.expectedHydrationSelection = this.getPrefilledStandingAuthSelection();

        // We want to suppress SLOA side effects when coming from edit flow
        this.suppressEditHydrationSelectionSideEffects = this.expectedHydrationSelection === 'yes'
          || this.expectedHydrationSelection === 'bundled'
          || this.expectedHydrationSelection === 'bundled-yes'
          || this.expectedHydrationSelection === 'bundled-no';

        buildFromPrefilledForm(
          this.prefilledPrompts,
          this.extraPromptsGroup,
          undefined,
          this.extraPromptsUpdatedInEdit
        );
      });

    templateId$
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(this.store.select(selectUseTemplate)),
        filter(([, useTemplate]) => useTemplate && !this.palAccountInContext)
      )
      .subscribe(() => {
        this.extraPromptsComplete.emit(false);
      });

    combineLatest([templateId$, extraPromptsReady$])
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(this.store.select(selectUseTemplate), this.store.select(selectTemplateStartTransactionStarted)),
        filter(
          ([[[id, isFromEdit, _list], complete], useTemplate, started]) =>
            useTemplate && !!id && complete && !isFromEdit
        ),
        delay(200)
      )
      .subscribe(([[[id, _isFromEdit, list], complete], _, started]) => {
        this.extraPromptsComplete.emit(complete);
        const selectedDraft = list.find((x) => x.draftId === id);
        if (selectedDraft) {
          if (started) {
            this.scrollToTop();
          }
          const mapRecipientFromDraft = selectedDraft.draftObject.isIsoCompliant || selectedDraft.draftObject.isThirdParty;
          this.store.dispatch(
            TransactionFormActions.templatePrefilledFlowStarted({
              response: selectedDraft,
              mapRecipientFromDraft
            })
          );
        }
      });

    // Template start transaction flow from transaction table
    combineLatest([this.prefilledPromptsObs, this.store.select(selectTemplateStartTransactionStarted)])
      .pipe(
        takeUntil(this.destroy$),
        filter(([prefilled, started]) => started && !!prefilled),
        map(([prefilled, started]) => prefilled)
      )
      .subscribe((prefilledForm) => {
        buildFromPrefilledForm(prefilledForm, this.extraPromptsGroup);
      });

    // BUILD EXTRAPROMPTS WITH PREFILLED PROMPT WHEN PREFILLEDPROMPTSOBS EMITS OR WHEN START TRANSACTION WIRE IS SELECTED
    combineLatest([
      this.prefilledPromptsObs,
      this.store.select(selectStandingAuthTableStartTransactionClickedWire),
    ])
      .pipe(
        takeUntil(this.destroy$),
        filter(([extraPrompts, startTransactionClicked]) => !!extraPrompts && startTransactionClicked),
        map(([extraprompts, _startTransactionClicked]) => extraprompts),
        distinctUntilChanged()
      )
      .subscribe((extraPrompts) => {
        if (extraPrompts) {
          buildFromPrefilledForm(extraPrompts, this.extraPromptsGroup);
          this.callStandingAuthorizations();
        }
      });

    /*
      Calling WireInputV3 before standingAuthorization call clears serviceError section in store thus clearing error banner
      Calling wireInputV3 only when standingAuthorization call is successful and startTransaction is clicked
    */
    this.store.select(selectStandingAuthCallSucceeded)
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(this.store.select(selectStandingAuthTableStartTransactionClickedWire), this.prefilledPromptsObs),
        filter(([callSucceeded, startTransactionClicked, _extraPrompts]) => callSucceeded && startTransactionClicked),
        map(([_callSucceeded, _startTransactionClicked, extraPrompts]) => extraPrompts)
      )
      .subscribe((extraPrompts) => {
        this.showStandingAuthSelection = true;
        this.callWireInputV3(extraPrompts.standingAuthId ?? '');
        this.store.dispatch(StandingAuthorizationsTableActions.startTransacionFlowComplete());
      });
  }

  ngOnDestroy(): void {
    this.parentForm.removeControl('useStandingAuth');
    this.parentForm.removeControl('useBundledStandingAuth');

    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  scrollToTop(): void {
    const elem = document.getElementById('transaction-form-header');
    setTimeout(() => {
      elem?.focus();
      elem?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'start'
      });
    });
  }

  callWireInputV3(standingAuthId: string): void {
    if (!standingAuthId) {
      this.store.dispatch(ModifiedFirstPartiesActions.getModifiedFirstParties({
        subaccount: this.subAccountId,
        transactionType: 'wires'
      }));
    }
    this.store.dispatch(WireActions.callWireInputV3({
      standingAuthId,
      isPalAccountInContext: false
    }));
    if (this.isPtrs) {
      this.store.dispatch(PtrsActions.callPtrs({ accountId: this.subAccountId }));
    }
  }

  callWireInputPAL(): void {
    this.store.dispatch(WireActions.callWireInputV3({
      standingAuthId: '',
      isPalAccountInContext: true
    }));
  }

  callStandingAuthorizations(bundled: boolean = false): void {
    const standingAuthRequest: StandingAuthorizationsRequest = {
      subAccountId: this.subAccountId,
      masterAccountId: this.masterAccountId,
      sloaType: bundled ? 'bundledwire' : 'W',
    };
    this.store.dispatch(StandingAuthorizationsActions.loadSloas(standingAuthRequest));
  }

  areExtraPromptsReady(
    isValid: boolean,
    isPtrs: boolean,
    ptrsCallFinished: boolean,
    inputCallV3Finished: boolean
  ): boolean {
    if (isPtrs) {
      return isValid && inputCallV3Finished && ptrsCallFinished;
    } else {
      return isValid && inputCallV3Finished;
    }
  }

  get useStandingAuth(): UntypedFormControl {
    return this.extraPromptsGroup?.get('useStandingAuth') as UntypedFormControl;
  }

  get useBundledStandingAuth(): UntypedFormControl {
    return this.extraPromptsGroup?.get('useBundledStandingAuth') as UntypedFormControl;
  }

  get extraPromptsGroup(): UntypedFormGroup {
    return this.parentForm?.get('extraPrompts') as UntypedFormGroup;
  }

  get useTemplates(): UntypedFormControl {
    return this.extraPromptsGroup?.get('useTemplates') as UntypedFormControl;
  }

  get selectedTemplateControl(): UntypedFormControl {
    return this.extraPromptsGroup?.get('templateId') as UntypedFormControl;
  }

  get showBundledPrompts(): boolean {
    return this.hasBundledOffer;
  }

  get showWireTemplates(): boolean {
    if (this.palAccountInContext) {
      return true;
    }
    if (this.enableWireTemplates && !this.showBundledPrompts) {
      return true;
    }
    if (this.enableWireTemplates && this.showBundledPrompts && this.standingAuthSelection === 'no') {
      return true;
    } else {
      return false;
    }
  }

  private getPrefilledStandingAuthSelection(): string | null {
    if (!this.prefilledPrompts) {
      return null;
    }

    if (this.prefilledPrompts.useStandingAuth === 'bundled') {
      if (this.prefilledPrompts.useBundledStandingAuth === 'yes') {
        return 'bundled-yes';
      }

      if (this.prefilledPrompts.useBundledStandingAuth === 'no') {
        return 'bundled-no';
      }

      return 'bundled';
    }

    return this.prefilledPrompts.useStandingAuth ?? null;
  }
}
